import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';

import { useTheme } from '../../theme/ThemeContext';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { AIExplanationDisplay } from './AIExplanationDisplay';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { Button } from '../common/Button';
import type { AppDispatch } from '../../store';
import {
  fetchStudyGroupRecommendations,
  generateOptimalGroupFormation,
  selectStudyGroupRecommendations,
  selectHighCompatibilityGroupRecommendations,
  selectStudyGroupLoading,
  selectStudyGroupError,
  selectOptimizationInProgress,
  type StudyGroupRecommendation,
} from '../../store/slices/studyGroupSlice';

interface StudyGroupRecommendationsProps {
  userId: string;
  onGroupSelect: (recommendation: StudyGroupRecommendation) => void;
  onJoinGroup: (groupId: string) => void;
  showOptimization?: boolean;
  maxRecommendations?: number;
}

export const StudyGroupRecommendations: React.FC<
  StudyGroupRecommendationsProps
> = ({
  userId,
  onGroupSelect,
  onJoinGroup,
  showOptimization = true,
  maxRecommendations = 10,
}) => {
  const { theme } = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const recommendations = useSelector(selectStudyGroupRecommendations);
  const highCompatibilityRecommendations = useSelector(
    selectHighCompatibilityGroupRecommendations
  );
  const loading = useSelector(selectStudyGroupLoading);
  const error = useSelector(selectStudyGroupError);
  const optimizationInProgress = useSelector(selectOptimizationInProgress);

  const [showHighCompatibilityOnly, setShowHighCompatibilityOnly] =
    useState(false);
  const [expandedExplanations, setExpandedExplanations] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    dispatch(fetchStudyGroupRecommendations(userId));
  }, [dispatch, userId]);

  const handleOptimizeGroupFormation = async () => {
    try {
      // Mock data for optimization - in real implementation, this would come from user profile
      const optimizationParams = {
        members: [userId],
        learningGoals: ['espresso_mastery', 'latte_art', 'brewing_consistency'],
        skillLevels: { [userId]: 0.6 },
        preferences: {
          groupSize: 'small_group',
          timeZone: 'UTC-8',
          availability: ['evening', 'weekend'],
          learningStyle: 'collaborative',
        },
      };

      await dispatch(
        generateOptimalGroupFormation(optimizationParams)
      ).unwrap();
      Alert.alert(
        'Optimization Complete',
        'AI has generated optimized study group recommendations based on your profile and preferences.'
      );
    } catch (err) {
      Alert.alert(
        'Error',
        'Failed to optimize group formation. Please try again.'
      );
    }
  };

  const toggleExplanation = (groupId: string) => {
    const newExpanded = new Set(expandedExplanations);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedExplanations(newExpanded);
  };

  const getCompatibilityColor = (score: number) => {
    if (score >= 0.8) return theme.colors.success;
    if (score >= 0.6) return theme.colors.warning;
    return theme.colors.error;
  };

  const getSkillLevelIcon = (skillLevel: string) => {
    switch (skillLevel) {
      case 'beginner':
        return 'leaf-outline';
      case 'intermediate':
        return 'bar-chart-outline';
      case 'advanced':
        return 'trophy-outline';
      default:
        return 'help-circle-outline';
    }
  };

  const renderRecommendationItem = ({
    item,
  }: {
    item: StudyGroupRecommendation;
  }) => {
    const isExpanded = expandedExplanations.has(item.group.id);
    const compatibilityColor = getCompatibilityColor(item.compatibilityScore);

    return (
      <View
        style={[
          styles.recommendationCard,
          { backgroundColor: theme.colors.surface },
        ]}
      >
        {/* Header */}
        <View style={styles.cardHeader}>
          <View style={styles.groupInfo}>
            <Text style={[styles.groupName, { color: theme.colors.text }]}>
              {item.group.name}
            </Text>
            <Text
              style={[
                styles.groupDescription,
                { color: theme.colors.textSecondary },
              ]}
            >
              {item.group.description}
            </Text>
          </View>
          <View style={styles.scoreContainer}>
            <ConfidenceIndicator
              confidence={item.compatibilityScore}
              size='small'
              showLabel={false}
            />
            <Text style={[styles.scoreText, { color: compatibilityColor }]}>
              {Math.round(item.compatibilityScore * 100)}%
            </Text>
          </View>
        </View>

        {/* Group Stats */}
        <View style={styles.groupStats}>
          <View style={styles.statItem}>
            <Ionicons
              name='people'
              size={16}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.statText, { color: theme.colors.textSecondary }]}
            >
              {item.group.currentMembers}/{item.group.maxMembers}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Ionicons
              name={getSkillLevelIcon(item.group.skillLevel)}
              size={16}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.statText, { color: theme.colors.textSecondary }]}
            >
              {item.group.skillLevel}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Ionicons
              name='trending-up'
              size={16}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.statText, { color: theme.colors.textSecondary }]}
            >
              {Math.round(item.group.averageEngagement * 100)}% active
            </Text>
          </View>
        </View>

        {/* Matching Factors */}
        <View style={styles.matchingFactors}>
          <Text
            style={[styles.factorsLabel, { color: theme.colors.textSecondary }]}
          >
            Why this group matches:
          </Text>
          <View style={styles.factorTags}>
            {item.matchingFactors.slice(0, 3).map((factor, index) => (
              <View
                key={index}
                style={[
                  styles.factorTag,
                  { backgroundColor: theme.colors.primaryLight },
                ]}
              >
                <Text
                  style={[styles.factorText, { color: theme.colors.primary }]}
                >
                  {factor.replace(/_/g, ' ')}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Common Interests */}
        {item.commonInterests.length > 0 && (
          <View style={styles.commonInterests}>
            <Text
              style={[
                styles.interestsLabel,
                { color: theme.colors.textSecondary },
              ]}
            >
              Common interests:
            </Text>
            <View style={styles.interestTags}>
              {item.commonInterests.map((interest, index) => (
                <View
                  key={index}
                  style={[
                    styles.interestTag,
                    { backgroundColor: theme.colors.successLight },
                  ]}
                >
                  <Text
                    style={[
                      styles.interestText,
                      { color: theme.colors.success },
                    ]}
                  >
                    {interest.replace(/_/g, ' ')}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* AI Explanation */}
        <TouchableOpacity
          style={styles.explanationToggle}
          onPress={() => toggleExplanation(item.group.id)}
        >
          <Ionicons
            name='bulb-outline'
            size={16}
            color={theme.colors.primary}
          />
          <Text
            style={[
              styles.explanationToggleText,
              { color: theme.colors.primary },
            ]}
          >
            {isExpanded ? 'Hide AI reasoning' : 'Show AI reasoning'}
          </Text>
          <Ionicons
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={16}
            color={theme.colors.primary}
          />
        </TouchableOpacity>

        {isExpanded && (
          <AIExplanationDisplay
            explanation={item.aiReasoning}
            confidence={item.confidence}
            factors={item.matchingFactors}
            style={styles.explanationDisplay}
          />
        )}

        {/* Actions */}
        <View style={styles.cardActions}>
          <Button
            title={item.group.isFull ? 'Full' : 'Join Group'}
            onPress={() => onJoinGroup(item.group.id)}
            variant='primary'
            size='small'
            disabled={item.group.isFull}
            icon='add-circle'
          />
          <TouchableOpacity
            style={[styles.detailsButton, { borderColor: theme.colors.border }]}
            onPress={() => onGroupSelect(item)}
          >
            <Text
              style={[
                styles.detailsButtonText,
                { color: theme.colors.primary },
              ]}
            >
              View Details
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  if (loading.recommendations) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingSpinner />
        <Text
          style={[styles.loadingText, { color: theme.colors.textSecondary }]}
        >
          Finding optimal study groups...
        </Text>
      </View>
    );
  }

  const displayRecommendations = showHighCompatibilityOnly
    ? highCompatibilityRecommendations
    : recommendations.slice(0, maxRecommendations);

  return (
    <View style={styles.container}>
      {/* Header Controls */}
      <View style={styles.headerControls}>
        <View style={styles.filterControls}>
          <TouchableOpacity
            style={[
              styles.filterButton,
              showHighCompatibilityOnly && {
                backgroundColor: theme.colors.primaryLight,
              },
            ]}
            onPress={() =>
              setShowHighCompatibilityOnly(!showHighCompatibilityOnly)
            }
          >
            <Ionicons
              name='star'
              size={16}
              color={
                showHighCompatibilityOnly
                  ? theme.colors.primary
                  : theme.colors.textSecondary
              }
            />
            <Text
              style={[
                styles.filterButtonText,
                {
                  color: showHighCompatibilityOnly
                    ? theme.colors.primary
                    : theme.colors.textSecondary,
                },
              ]}
            >
              High compatibility only
            </Text>
          </TouchableOpacity>
        </View>

        {showOptimization && (
          <Button
            title='Optimize Formation'
            onPress={handleOptimizeGroupFormation}
            variant='outline'
            size='small'
            icon='settings'
            loading={optimizationInProgress}
            disabled={optimizationInProgress}
          />
        )}
      </View>

      {/* Error Display */}
      {error && (
        <ErrorMessage
          message={error}
          onRetry={() => dispatch(fetchStudyGroupRecommendations(userId))}
        />
      )}

      {/* Recommendations List */}
      <FlatList
        data={displayRecommendations}
        renderItem={renderRecommendationItem}
        keyExtractor={item => item.group.id}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons
              name='people-outline'
              size={64}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.emptyText, { color: theme.colors.textSecondary }]}
            >
              No study group recommendations
            </Text>
            <Text
              style={[
                styles.emptySubtext,
                { color: theme.colors.textSecondary },
              ]}
            >
              Complete your skill assessment to get personalized group
              suggestions
            </Text>
          </View>
        }
      />

      {/* Optimization Status */}
      {optimizationInProgress && (
        <View
          style={[
            styles.optimizationStatus,
            { backgroundColor: theme.colors.primaryLight },
          ]}
        >
          <LoadingSpinner size='small' />
          <Text
            style={[styles.optimizationText, { color: theme.colors.primary }]}
          >
            AI is optimizing group formations...
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerControls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  filterControls: {
    flex: 1,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 12,
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },
  listContainer: {
    padding: 16,
  },
  recommendationCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  groupInfo: {
    flex: 1,
  },
  groupName: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  groupDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  scoreContainer: {
    alignItems: 'center',
    marginLeft: 12,
  },
  scoreText: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 4,
  },
  groupStats: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  statText: {
    fontSize: 14,
    marginLeft: 4,
    textTransform: 'capitalize',
  },
  matchingFactors: {
    marginBottom: 12,
  },
  factorsLabel: {
    fontSize: 14,
    marginBottom: 8,
  },
  factorTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  factorTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  factorText: {
    fontSize: 12,
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  commonInterests: {
    marginBottom: 12,
  },
  interestsLabel: {
    fontSize: 14,
    marginBottom: 8,
  },
  interestTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  interestTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  interestText: {
    fontSize: 12,
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  explanationToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    marginBottom: 8,
  },
  explanationToggleText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
    marginRight: 6,
  },
  explanationDisplay: {
    marginBottom: 12,
  },
  cardActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailsButton: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  detailsButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  optimizationStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    margin: 16,
    borderRadius: 8,
  },
  optimizationText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 8,
  },
});
